import { authClient } from "./auth-client";

let cachedToken: string | null = null;
let tokenExpiresAt = 0;
let inflightRequest: Promise<string | null> | null = null;
const TOKEN_TTL_MS = 5 * 60 * 1000;

export async function getToken(): Promise<string | null> {
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;
  // Deduplicate concurrent callers — only one network request in flight at a time.
  if (!inflightRequest) {
    inflightRequest = authClient
      .getSession()
      .then((res) => {
        const data = res.data;
        cachedToken = data?.session?.token ?? null;
        tokenExpiresAt = Date.now() + TOKEN_TTL_MS;
        inflightRequest = null;
        return cachedToken;
      })
      .catch(() => {
        inflightRequest = null;
        return null;
      });
  }
  return inflightRequest;
}

export function invalidateToken(): void {
  cachedToken = null;
  tokenExpiresAt = 0;
  inflightRequest = null;
}

export class ApiError extends Error {
  public code: string;
  public details?: unknown;
  public errors?: Array<{ fieldName: string; message: string }>;
  public status: number;

  constructor(
    status: number,
    message: string,
    data?: {
      code?: string;
      message?: string;
      details?: unknown;
      errors?: Array<{ fieldName: string; message: string }>;
    },
  ) {
    super(data?.message || message);
    this.status = status;
    this.name = "ApiError";
    this.code = data?.code || "UNKNOWN_ERROR";
    this.details = data?.details;
    this.errors = data?.errors;
  }
}

export async function fetchApi<T>(
  path: string,
  options: RequestInit & {
    skipRedirect?: boolean;
    responseType?: "json" | "text";
    searchParams?: Record<string, string>;
  } = {},
): Promise<T> {
  // 1. Build URL with optional search params
  const { searchParams, ...fetchOptions } = options;
  const url = searchParams
    ? `${path}?${new URLSearchParams(searchParams).toString()}`
    : path;

  // 2. Get current session token (cached, at most one network call per 5 min).
  const token = await getToken();

  // 3. Prepare Headers
  const headers = new Headers(fetchOptions.headers);
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  // Automatic Content-Type for JSON, but NOT for FormData (browser needs to set boundary)
  if (
    fetchOptions.body &&
    !headers.has("Content-Type") &&
    !(fetchOptions.body instanceof FormData)
  ) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  // 4. Centralized 401 Handling (Session Expired/Invalid)
  if (
    response.status === 401 &&
    typeof window !== "undefined" &&
    !fetchOptions.skipRedirect
  ) {
    console.error(
      "[API] 401 on",
      path,
      "— triggering redirect. cachedToken at time of redirect:",
      cachedToken ? "set" : "null",
    );
    // Sign out to clear local session state and invalidate token cache
    invalidateToken();
    await authClient.signOut();

    // Redirect to login with current location preserved
    window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;

    // Throw to stop further execution in the calling component/query
    throw new ApiError(401, "Session expired. Redirecting...");
  }

  // 4. Standard Error Handling
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      `API error: ${response.statusText}`,
      errorData,
    );
  }

  if (response.status === 204) {
    return undefined as unknown as T;
  }

  if (options.responseType === "text") {
    return response.text() as Promise<T>;
  }

  return response.json() as Promise<T>;
}
